import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { onboardingAPI } from '../../services/api';
import toast from 'react-hot-toast';

// Import all form components
import WaiverForm from './WaiverForm';
import PARQForm from './PARQForm';
import GoalsActivitiesForm from './GoalsActivitiesForm';
import MedicalHistoryForm from './MedicalHistoryForm';
import NutritionForm from './NutritionForm';
import LifestyleForm from './LifestyleForm';

interface OnboardingStatus {
  waiverCompleted: boolean;
  parqCompleted: boolean;
  goalsActivitiesCompleted: boolean;
  medicalHistoryCompleted: boolean;
  nutritionCompleted: boolean;
  lifestyleCompleted: boolean;
  isFullyCompleted: boolean;
}

const OnboardingWrapper: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [nextForm, setNextForm] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      const response = await onboardingAPI.getStatus();
      setStatus(response.data.status);
      setNextForm(response.data.nextForm);
      
      // If onboarding is complete, redirect to dashboard
      if (response.data.isComplete) {
        toast.success('Welcome to WebFit!');
        navigate('/dashboard');
        return;
      }
    } catch (error: any) {
      console.error('Error checking onboarding status:', error);
      toast.error('Failed to load onboarding status');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const handleFormComplete = async (formType: string) => {
    try {
      // Check status again to get the next form
      await checkOnboardingStatus();
      toast.success(`${formType} form completed successfully!`);
    } catch (error) {
      console.error('Error updating onboarding status:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  // Render the appropriate form based on nextForm
  const renderForm = () => {
    switch (nextForm) {
      case 'waiver':
        return <WaiverForm onComplete={() => handleFormComplete('Waiver')} />;
      case 'parq':
        return <PARQForm onComplete={() => handleFormComplete('PARQ')} />;
      case 'goals-activities':
        return <GoalsActivitiesForm onComplete={() => handleFormComplete('Goals & Activities')} />;
      case 'medical-history':
        return <MedicalHistoryForm onComplete={() => handleFormComplete('Medical History')} />;
      case 'nutrition':
        return <NutritionForm onComplete={() => handleFormComplete('Nutrition')} />;
      case 'lifestyle':
        return <LifestyleForm onComplete={() => handleFormComplete('Lifestyle')} />;
      default:
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Onboarding Complete!</h2>
              <p className="text-gray-600 mb-6">Welcome to WebFit! You're all set to start your fitness journey.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      {status && (
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-lg font-semibold text-gray-900">Complete Your Profile</h1>
              <span className="text-sm text-gray-500">
                {[status.waiverCompleted, status.parqCompleted, status.goalsActivitiesCompleted, status.medicalHistoryCompleted, status.nutritionCompleted, status.lifestyleCompleted].filter(Boolean).length} of 6 completed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${([status.waiverCompleted, status.parqCompleted, status.goalsActivitiesCompleted, status.medicalHistoryCompleted, status.nutritionCompleted, status.lifestyleCompleted].filter(Boolean).length / 6) * 100}%`
                }}
              ></div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span className={status.waiverCompleted ? 'text-blue-600 font-medium' : ''}>Waiver</span>
              <span className={status.parqCompleted ? 'text-blue-600 font-medium' : ''}>PAR-Q</span>
              <span className={status.goalsActivitiesCompleted ? 'text-blue-600 font-medium' : ''}>Goals</span>
              <span className={status.medicalHistoryCompleted ? 'text-blue-600 font-medium' : ''}>Medical</span>
              <span className={status.nutritionCompleted ? 'text-blue-600 font-medium' : ''}>Nutrition</span>
              <span className={status.lifestyleCompleted ? 'text-blue-600 font-medium' : ''}>Lifestyle</span>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      {renderForm()}
    </div>
  );
};

export default OnboardingWrapper;
